import { useCallback, useEffect, useMemo, useState } from "react";

/** Two-column section layout — keys grouped by which column they
 *  render in, in render order within each column. */
export interface SectionLayout {
  left: string[];
  right: string[];
}

interface DefaultLayout {
  left: readonly string[];
  right: readonly string[];
}

/**
 * Per-project section layout for the Company-detail page.
 * Persists which column each section sits in (left vs right) and
 * the within-column order to localStorage so each operator's
 * personal layout survives reloads.
 *
 * Storage:
 *   prism.{slug}.detail.layout =
 *     { "left": ["info","funding",…], "right": ["contacts",…] }
 *
 * Backward compat: an older "sectionOrder" entry from before the
 * cross-column move shipped is read on first mount and bucketed
 * into the left column (everything was left-only back then). The
 * legacy key is removed once the new structure is persisted.
 *
 * Forward compat: any default key not present in the stored
 * layout is appended at the end of its default column on read,
 * so adding a new section ships smoothly without forcing a reset.
 */
export function useSectionLayout(slug: string, defaults: DefaultLayout) {
  // Memoise the defaults tuple so the effect deps stay stable when
  // the caller passes a fresh literal each render.
  const def = useMemo<SectionLayout>(
    () => ({ left: [...defaults.left], right: [...defaults.right] }),
    [defaults],
  );

  const [layout, setLayout] = useState<SectionLayout>(() =>
    initialLayout(slug, def),
  );

  // Re-seed when the slug changes (project switch in the same tab).
  useEffect(() => {
    setLayout(initialLayout(slug, def));
  }, [slug, def]);

  const persist = useCallback(
    (next: SectionLayout) => {
      try {
        window.localStorage.setItem(storageKey(slug), JSON.stringify(next));
      } catch {
        // QuotaExceeded / disabled storage / private mode — silent.
      }
    },
    [slug],
  );

  /** Reorder within the same column. No-op when keys aren't both
   *  present in the column (defensive against stale dnd events). */
  const moveWithinColumn = useCallback(
    (column: keyof SectionLayout, activeKey: string, overKey: string) => {
      if (activeKey === overKey) return;
      setLayout((prev) => {
        const items = [...prev[column]];
        const oldIndex = items.indexOf(activeKey);
        const newIndex = items.indexOf(overKey);
        if (oldIndex < 0 || newIndex < 0) return prev;
        const [moved] = items.splice(oldIndex, 1);
        items.splice(newIndex, 0, moved);
        const next: SectionLayout = { ...prev, [column]: items };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  /** Move a section across columns. When `overKey` is provided
   *  the section lands at that key's index; otherwise it's
   *  appended to the destination column's tail (drop on empty
   *  column / on the column body itself). */
  const moveBetweenColumns = useCallback(
    (
      sectionKey: string,
      fromColumn: keyof SectionLayout,
      toColumn: keyof SectionLayout,
      overKey?: string,
    ) => {
      if (fromColumn === toColumn) return;
      setLayout((prev) => {
        const from = prev[fromColumn].filter((k) => k !== sectionKey);
        // Defensive dedupe — if the section is somehow already
        // present in the destination column (stale layout, view
        // replay, racy dnd events), strip it before splicing the
        // moved entry in. Without this, an in-between state could
        // leave the same key in both columns and the renderer
        // would draw the section twice.
        const to = prev[toColumn].filter((k) => k !== sectionKey);
        if (overKey) {
          const idx = to.indexOf(overKey);
          if (idx >= 0) to.splice(idx, 0, sectionKey);
          else to.push(sectionKey);
        } else {
          to.push(sectionKey);
        }
        const next: SectionLayout = {
          ...prev,
          [fromColumn]: from,
          [toColumn]: to,
        };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const reset = useCallback(() => {
    setLayout(def);
    persist(def);
  }, [def, persist]);

  /** Replace the layout wholesale — used when applying a saved
   *  layout view. Stale keys are filtered out and any default
   *  keys missing from the incoming layout are appended at the
   *  end of their default column. */
  const applyLayout = useCallback(
    (incoming: Partial<SectionLayout>) => {
      const next = appendMissing(
        {
          left: filterStringList(incoming.left),
          right: filterStringList(incoming.right),
        },
        def,
      );
      setLayout(next);
      persist(next);
    },
    [def, persist],
  );

  /** Identify which column a key currently lives in. Returns null
   *  when the key isn't in either column (renamed / deleted
   *  section). */
  const columnOf = useCallback(
    (key: string): keyof SectionLayout | null => {
      if (layout.left.includes(key)) return "left";
      if (layout.right.includes(key)) return "right";
      return null;
    },
    [layout],
  );

  return {
    layout,
    moveWithinColumn,
    moveBetweenColumns,
    reset,
    columnOf,
    applyLayout,
  };
}

function filterStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((s): s is string => typeof s === "string");
}

function storageKey(slug: string): string {
  return `prism.${slug}.detail.layout`;
}

function legacyOrderKey(slug: string): string {
  return `prism.${slug}.detail.sectionOrder`;
}

function initialLayout(
  slug: string,
  defaults: SectionLayout,
): SectionLayout {
  // Try the structured entry first.
  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (raw) {
      const parsed = JSON.parse(raw);
      const left = filterStrings(parsed?.left);
      const right = filterStrings(parsed?.right);
      if (left || right) {
        return appendMissing({ left: left ?? [], right: right ?? [] }, defaults);
      }
    }
  } catch {
    /* fall through */
  }

  // Backward-compat: the v1 "sectionOrder" entry was a single
  // array, all-left. Bucket it and clear the legacy key.
  try {
    const legacyRaw = window.localStorage.getItem(legacyOrderKey(slug));
    if (legacyRaw) {
      const parsed = JSON.parse(legacyRaw);
      if (Array.isArray(parsed)) {
        const left = parsed.filter(
          (s): s is string => typeof s === "string",
        );
        try {
          window.localStorage.removeItem(legacyOrderKey(slug));
        } catch {
          /* ignore */
        }
        return appendMissing({ left, right: [] }, defaults);
      }
    }
  } catch {
    /* fall through */
  }

  return defaults;
}

function filterStrings(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((s): s is string => typeof s === "string");
}

/** Walk the stored layout left → right, dedupe across columns
 *  on the fly, then append any default keys we haven't seen
 *  yet. Two-fold guarantee: every key appears at most once
 *  across both columns (so the renderer can't double-draw a
 *  section), and any default key the saved layout doesn't
 *  carry lands at the end of its default column on first read
 *  (so newly-added sections show up without forcing a reset). */
function appendMissing(
  stored: SectionLayout,
  defaults: SectionLayout,
): SectionLayout {
  const seen = new Set<string>();
  const left: string[] = [];
  for (const k of stored.left) {
    if (seen.has(k)) continue;
    seen.add(k);
    left.push(k);
  }
  const right: string[] = [];
  for (const k of stored.right) {
    if (seen.has(k)) continue;
    seen.add(k);
    right.push(k);
  }
  for (const k of defaults.left) {
    if (seen.has(k)) continue;
    seen.add(k);
    left.push(k);
  }
  for (const k of defaults.right) {
    if (seen.has(k)) continue;
    seen.add(k);
    right.push(k);
  }
  return { left, right };
}
